import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import SearchIcon from "@mui/icons-material/Search";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { HideIfNoRole } from "../../../auth/authUi";
import AxionControls from "../../../components/controls/AxionControls";
import {
  activateStorageLocation,
  deactivateStorageLocation,
  getStorageLocations,
} from "../../../fetchers/cmmsStorageLocation";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { CMMS_MANAGER_ROLES } from "../../cmms/cmmsRoles";
import cmmsMessages from "../../cmms/cmmsMessages";
import { CmmsEmptyState, CmmsErrorState, formatCmmsError } from "../../cmms/components/CmmsStates";
import {
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getSwitchSx,
} from "../../cmms/assetHierarchyStyles";
import messages from "../messages";
import StorageLocationsDataGrid from "./StorageLocationsDataGrid";
import StorageLocationFormDialog from "./StorageLocationFormDialog";

/*
 * The storage locations tab of the CMMS settings page.
 *
 * The spare parts register's panel, cut to what a list of shelves needs. No statistic tile: there
 * is no number on this table worth a tile, and a count of parts per location is a second read of
 * the whole register to answer a question nobody is blocked on — retiring a location is never
 * refused, and the parts on it keep naming it either way.
 *
 * The query key is shared with the spare part form's location dropdown on purpose. Adding a shelf
 * here invalidates it, so the register offers the new one without a reload.
 */
export default function StorageLocationsPanel() {
  const theme = useTheme();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const [companyKey] = useLocalStorage("companyKey", 1);

  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [formState, setFormState] = useState({ open: false, storageLocation: null });
  const [confirmation, setConfirmation] = useState(null);

  const locationsQuery = useQuery(
    ["cmmsStorageLocations", companyKey, includeInactive],
    () => getStorageLocations(companyKey, includeInactive),
    { enabled: Boolean(companyKey), staleTime: 30000 },
  );

  const locations = useMemo(() => locationsQuery.data ?? [], [locationsQuery.data]);

  // The same division the register draws: the endpoint owns which rows exist — that is what the
  // switch below asks it — and the browser owns which of them are on screen. The list arrives
  // whole, so the text is here to be matched.
  const visibleLocations = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return locations;
    return locations.filter((location) => (location.name ?? "").toLowerCase().includes(needle));
  }, [locations, searchText]);

  const deactivateMutation = useMutation({
    mutationFn: (id) => deactivateStorageLocation(companyKey, id),
  });

  const restoreMutation = useMutation({
    mutationFn: (id) => activateStorageLocation(companyKey, id),
  });

  const hasActiveFilters = includeInactive || searchText.trim() !== "";

  const handleClearFilters = () => {
    setIncludeInactive(false);
    setSearchText("");
  };

  const handleAdd = () => setFormState({ open: true, storageLocation: null });
  const handleEdit = (row) => setFormState({ open: true, storageLocation: row });
  const closeForm = () => setFormState({ open: false, storageLocation: null });

  const handleDeactivate = (row) => {
    setConfirmation({
      title: intl.formatMessage(messages.deactivateTitle),
      message: intl.formatMessage(messages.deactivateMessage, { name: row.name }),
      confirmText: intl.formatMessage(messages.deactivateConfirm),
      onConfirm: async () => {
        try {
          await deactivateMutation.mutateAsync(row.id);
          setConfirmation(null);
          await queryClient.invalidateQueries({ queryKey: ["cmmsStorageLocations"] });
          pushNotification(intl.formatMessage(messages.toastDeactivated));
        } catch (error) {
          pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.deactivateFailed)));
        }
      },
    });
  };

  const handleRestore = (row) => {
    setConfirmation({
      title: intl.formatMessage(messages.restoreTitle),
      message: intl.formatMessage(messages.restoreMessage, { name: row.name }),
      confirmText: intl.formatMessage(messages.restoreConfirm),
      onConfirm: async () => {
        try {
          await restoreMutation.mutateAsync(row.id);
          setConfirmation(null);
          await queryClient.invalidateQueries({ queryKey: ["cmmsStorageLocations"] });
          pushNotification(intl.formatMessage(messages.toastRestored));
        } catch (error) {
          pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.restoreFailed)));
        }
      },
    });
  };

  const isMutating = deactivateMutation.isPending || deactivateMutation.isLoading
    || restoreMutation.isPending || restoreMutation.isLoading;

  return (
    <Box>
      {/*
        * No heading of its own — the tab above already says Storage locations, and repeating it
        * two lines down is a title nobody reads twice. The line that follows says something the
        * label cannot.
        */}
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        <FormattedMessage {...messages.storageLocationsSubtitle} />
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ gap: 1, mb: 2 }}>
        {/*
          * Behind the route's own Admin gate as well. Belt and braces on purpose: the gate is one
          * line in AppRoutes.js, and a button that 403s is worse than one that was never offered.
          */}
        {/*
          * The module's own button styling, not MUI's default. palette.primary.main is
          * colors.primary[500] and so is the surface behind it — the same hex — so a bare
          * contained button is invisible until hovered.
          */}
        <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={getContainedButtonSx(cmmsColors)}
          >
            <FormattedMessage {...messages.addStorageLocation} />
          </Button>
        </HideIfNoRole>

        <TextField
          size="small"
          sx={{ width: { xs: "100%", sm: 280 } }}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          label={intl.formatMessage(messages.filterSearch)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/*
          * Server-side, and the only way to a retired row: the endpoint leaves them out by
          * default, so without this switch there is no way to reach the restore button.
          */}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
              sx={getSwitchSx(cmmsColors)}
            />
          }
          label={intl.formatMessage(messages.filterShowInactive)}
        />
      </Stack>

      {locationsQuery.error &&
        <CmmsErrorState
          error={locationsQuery.error}
          fallback={intl.formatMessage(messages.loadFailed)}
          onRetry={() => locationsQuery.refetch()}
          isRetrying={locationsQuery.isFetching}
        />
      }
      {!locationsQuery.error &&
        <StorageLocationsDataGrid
          storageLocations={visibleLocations}
          // First load only, so a background refetch does not replace the list with a skeleton.
          isLoading={locationsQuery.isLoading}
          showStatusColumn={includeInactive}
          onEdit={handleEdit}
          onDeactivate={handleDeactivate}
          onRestore={handleRestore}
          emptyState={
            hasActiveFilters
              ? <CmmsEmptyState
                  icon={<FilterAltOffIcon />}
                  title={intl.formatMessage(messages.emptyFilteredTitle)}
                  hint={intl.formatMessage(messages.emptyFilteredHint)}
                  action={
                    <Button size="small" variant="outlined" color="secondary" onClick={handleClearFilters}>
                      <FormattedMessage {...messages.filterClear} />
                    </Button>
                  }
                />
              : <CmmsEmptyState
                  icon={<WarehouseIcon />}
                  title={intl.formatMessage(messages.emptyTitle)}
                  hint={intl.formatMessage(messages.emptyHint)}
                  action={
                    <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                        sx={getContainedButtonSx(cmmsColors)}
                      >
                        <FormattedMessage {...messages.addStorageLocation} />
                      </Button>
                    </HideIfNoRole>
                  }
                />
          }
        />
      }

      <StorageLocationFormDialog
        open={formState.open}
        storageLocation={formState.storageLocation}
        companyKey={companyKey}
        onClose={closeForm}
      />

      <AxionControls.AxionConfirmation
        open={Boolean(confirmation)}
        title={confirmation?.title}
        message={confirmation?.message}
        confirmText={confirmation?.confirmText ?? intl.formatMessage(cmmsMessages.confirm)}
        cancelText={intl.formatMessage(cmmsMessages.cancel)}
        onConfirm={confirmation?.onConfirm}
        onCancel={() => setConfirmation(null)}
      />
      <AxionControls.AxionBackdrop open={isMutating} />
    </Box>
  );
}
